import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Paper, Tabs, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'recompose';
import { get } from 'lodash';

import { ClientFollowMapping, ClientFollowMappingList } from 'api';
import { LargeTab, Queries } from 'components';
import * as paths from 'paths';
import * as Links from 'Links';

import { ASSET_KEYS } from '../components/Onboarding/constants';
import Company from '../components/Onboarding/Company';
import Custom from '../components/Onboarding/Custom';
import Create from '../components/Onboarding/Custom/Create';
import EnableSwitch from '../components/Onboarding/EnableSwitch';

interface IPropsEnhanced extends RouteComponentProps, WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    help: {
      fontWeight: theme.typography.fontWeightRegular,
      marginBottom: theme.spacing(3),
    },
    wrapper: {
      padding: theme.spacing(2),
    },
    divider: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    customText: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(3),
    },
    status: {
      marginRight: theme.spacing(2),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withRouter, withStyles(styles));

const Onboarding: FC<IPropsEnhanced> = ({ classes, match }) => {
  const { t } = useTranslation();
  const isCompanyOnboarding = match.path === paths.adminSettingsCompanyOnboarding;
  const isCustomOnboarding = match.path === paths.adminSettingsCustomOnboarding;

  function getCompanyMapping(mappingList: ClientFollowMappingList) {
    const { client_follow_mappings } = mappingList;
    return client_follow_mappings.find(mapping => mapping.all_users);
  }

  function getCustomMappings(mappingList: ClientFollowMappingList) {
    const { client_follow_mappings } = mappingList;
    return client_follow_mappings.filter(mapping => !mapping.all_users);
  }

  function hasAssetKeys(mapping: ClientFollowMapping) {
    return !!ASSET_KEYS.map(key => get(mapping, key, []).length).reduce(
      (prev, next) => prev + next,
      0,
    );
  }

  function renderTabs(toggle: JSX.Element) {
    return (
      <Grid container alignItems="center">
        <Grid xs item>
          <Tabs value={match.path}>
            <LargeTab
              component={Links.AdminSettingsCompanyOnboarding}
              label={<Fragment>{t('common:company')}</Fragment>}
              value={paths.adminSettingsCompanyOnboarding}
            />
            <LargeTab
              component={Links.AdminSettingsCustomOnboarding}
              label={<Fragment>{t('common:custom')}</Fragment>}
              value={paths.adminSettingsCustomOnboarding}
            />
          </Tabs>
        </Grid>
        {!!toggle && (
          <Grid item className={classes.status}>
            {toggle}
          </Grid>
        )}
      </Grid>
    );
  }

  function renderCompany(followMappings: ClientFollowMappingList) {
    const mapping = getCompanyMapping(followMappings);
    const isValid = hasAssetKeys(mapping);
    const toggle = isValid ? <EnableSwitch mapping={mapping} /> : null;

    return (
      <Paper>
        {renderTabs(toggle)}
        <Divider />
        <div className={classes.wrapper}>
          <Typography className={classes.help} variant="subtitle2">
            {t('admin:onboardingCompanyHelp')} {t('admin:onboardingHelp')}
          </Typography>
          <Divider className={classes.divider} />
          {!!mapping && <Company mapping={mapping} />}
        </div>
      </Paper>
    );
  }

  function renderCustom(followMappings: ClientFollowMappingList) {
    return (
      <Fragment>
        <Paper>
          {renderTabs(null)}
          <Divider />
          <div className={classes.wrapper}>
            <Typography className={classes.help} variant="subtitle2" paragraph>
              {t('admin:onboardingCustomHelp')} {t('admin:onboardingHelp')}
            </Typography>
            <Create />
          </div>
        </Paper>
        <Typography variant="subtitle2" className={classes.customText}>
          {t('admin:onboardingCustomHeader')}
        </Typography>
        <Custom mappings={getCustomMappings(followMappings)} />
      </Fragment>
    );
  }

  return (
    <Queries.GetClientFollowMappings>
      {followMappings => (
        <Fragment>
          {isCompanyOnboarding && renderCompany(followMappings)}
          {isCustomOnboarding && renderCustom(followMappings)}
        </Fragment>
      )}
    </Queries.GetClientFollowMappings>
  );
};

export default enhancers(Onboarding);
